import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    GraduationCap, 
    ArrowLeft,
    Award,
    AlertTriangle,
    Plus,
    Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const qualStatusColors = {
    current: 'status-available',
    expiring_soon: 'status-warning',
    expired: 'status-unavailable'
};

const qualStatusLabels = {
    current: 'Current',
    expiring_soon: 'Expiring Soon',
    expired: 'Expired'
};

export default function TrainingPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [qualifications, setQualifications] = useState([]);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        fetchQualifications();
    }, [user]);
    
    const fetchQualifications = async () => {
        try {
            const response = await axios.get(`${API_URL}/qualifications`, {
                params: { user_id: user?.id }
            });
            setQualifications(response.data);
        } catch (error) {
            console.error('Failed to fetch qualifications:', error);
            toast.error('Failed to load qualifications');
        } finally {
            setLoading(false);
        }
    };
    
    const currentQuals = qualifications.filter(q => q.status === 'current');
    const expiringQuals = qualifications.filter(q => q.status === 'expiring_soon');
    const expiredQuals = qualifications.filter(q => q.status === 'expired');
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background p-4">
                <div className="space-y-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="skeleton h-20 rounded-lg"></div>
                    ))}
                </div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-background" data-testid="training-page">
            {/* Header */}
            <header className="bg-card border-b border-border sticky top-0 z-30">
                <div className="flex items-center h-14 px-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/portal')} data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-lg font-semibold ml-2">Training & Qualifications</h1>
                </div>
            </header>
            
            <div className="p-4 space-y-6 pb-20">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-3">
                    <Card className="bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                        <CardContent className="p-3 text-center">
                            <p className="text-2xl font-bold text-green-700 dark:text-green-400">{currentQuals.length}</p>
                            <p className="text-xs text-green-600 dark:text-green-500">Current</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                        <CardContent className="p-3 text-center">
                            <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">{expiringQuals.length}</p>
                            <p className="text-xs text-amber-600 dark:text-amber-500">Expiring</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800">
                        <CardContent className="p-3 text-center">
                            <p className="text-2xl font-bold text-red-700 dark:text-red-400">{expiredQuals.length}</p>
                            <p className="text-xs text-red-600 dark:text-red-500">Expired</p>
                        </CardContent>
                    </Card>
                </div>
                
                {/* Expiring Soon Alert */}
                {expiringQuals.length > 0 && (
                    <Card className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <AlertTriangle className="w-5 h-5 text-amber-600" />
                                <div>
                                    <p className="font-medium text-amber-800 dark:text-amber-300">Qualifications Expiring Soon</p>
                                    <p className="text-sm text-amber-700 dark:text-amber-400">
                                        {expiringQuals.map(q => q.name).join(', ')}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}
                
                {/* All Qualifications */}
                <div>
                    <h2 className="font-semibold mb-3">My Qualifications</h2>
                    {qualifications.length > 0 ? (
                        <div className="space-y-3">
                            {qualifications.map((qual) => (
                                <Card key={qual.id}>
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-3">
                                                <div className={`p-2 rounded-lg ${
                                                    qual.status === 'current' ? 'bg-green-100 dark:bg-green-900/30' :
                                                    qual.status === 'expiring_soon' ? 'bg-amber-100 dark:bg-amber-900/30' :
                                                    'bg-red-100 dark:bg-red-900/30'
                                                }`}>
                                                    <Award className={`w-5 h-5 ${
                                                        qual.status === 'current' ? 'text-green-600' :
                                                        qual.status === 'expiring_soon' ? 'text-amber-600' :
                                                        'text-red-600'
                                                    }`} />
                                                </div>
                                                <div>
                                                    <h3 className="font-medium">{qual.name}</h3>
                                                    {qual.issuing_body && (
                                                        <p className="text-sm text-muted-foreground">{qual.issuing_body}</p>
                                                    )}
                                                    <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                                                        <Calendar className="w-3 h-3" />
                                                        <span>Issued: {format(new Date(qual.issue_date), 'MMM d, yyyy')}</span>
                                                    </div>
                                                    {qual.expiry_date && (
                                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                            <Calendar className="w-3 h-3" />
                                                            <span>Expires: {format(new Date(qual.expiry_date), 'MMM d, yyyy')}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <Badge className={`status-badge ${qualStatusColors[qual.status]}`}>
                                                {qualStatusLabels[qual.status]}
                                            </Badge>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <Card>
                            <CardContent className="py-8 text-center text-muted-foreground">
                                <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>No qualifications recorded</p>
                                <p className="text-sm mt-1">Contact your unit administrator to add your qualifications</p>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
